import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { X, GitBranch, Loader2 } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const FlowSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muy corto!").max(100, "Muy largo!").required("Requerido"),
  description: Yup.string(),
  triggerType: Yup.string().oneOf(["keyword", "all_new", "queue"]).required(),
  triggerValue: Yup.string().when("triggerType", {
    is: "keyword",
    then: (s) => s.required("Ingresa al menos una palabra clave"),
  }),
  isDefault: Yup.boolean(),
  isActive: Yup.boolean(),
});

const BotFlowModal = ({ open, onClose, flow, whatsappId, onSave }) => {
  const initialState = {
    name: "",
    description: "",
    triggerType: "keyword",
    triggerValue: "",
    isDefault: false,
    isActive: true,
  };

  const [initialValues, setInitialValues] = useState(initialState);

  useEffect(() => {
    if (flow) {
      setInitialValues({
        name: flow.name || "",
        description: flow.description || "",
        triggerType: flow.triggerType || "keyword",
        triggerValue: flow.triggerValue || "",
        isDefault: flow.isDefault || false,
        isActive: flow.isActive !== false,
      });
    } else {
      setInitialValues(initialState);
    }
  }, [flow, open]);

  const handleClose = () => {
    setInitialValues(initialState);
    onClose();
  };

  const handleSave = async (values) => {
    try {
      const payload = { ...values, whatsappId };
      if (flow) {
        await api.put(`/bot/flows/${flow.id}`, payload);
        toast.success("Flujo actualizado");
      } else {
        await api.post("/bot/flows", payload);
        toast.success("Flujo creado");
      }
      if (onSave) onSave();
      handleClose();
    } catch (err) { toastError(err); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-indigo-600" />
            {flow ? "Editar Flujo" : "Nuevo Flujo"}
          </h2>
          <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={FlowSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => { handleSave(values); actions.setSubmitting(false); }, 400);
          }}
        >
          {({ errors, touched, isSubmitting, values }) => (
            <Form>
              <div className="px-5 py-4 space-y-4 bg-slate-50 dark:bg-slate-800/30">
                <div>
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Nombre del flujo</label>
                  <Field
                    name="name"
                    autoFocus
                    placeholder="Ej: Bienvenida, Soporte, Ventas"
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                      touched.name && errors.name ? "border-red-400" : "border-slate-200 dark:border-slate-700"
                    }`}
                  />
                  {touched.name && errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Descripción</label>
                  <Field
                    as="textarea"
                    name="description"
                    rows={2}
                    placeholder="Describe qué hace este flujo"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Tipo de activación</label>
                  <Field
                    as="select"
                    name="triggerType"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="keyword">Palabra clave</option>
                    <option value="all_new">Todos los mensajes nuevos</option>
                    <option value="queue">Por cola</option>
                  </Field>
                </div>
                {values.triggerType === "keyword" && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Palabras clave</label>
                    <Field
                      name="triggerValue"
                      placeholder="hola,menu,ayuda (separadas por coma)"
                      className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                        touched.triggerValue && errors.triggerValue ? "border-red-400" : "border-slate-200 dark:border-slate-700"
                      }`}
                    />
                    {touched.triggerValue && errors.triggerValue && <p className="text-xs text-red-500 mt-1">{errors.triggerValue}</p>}
                    <p className="text-xs text-slate-400 mt-1">Separa múltiples palabras con comas</p>
                  </div>
                )}
                {values.triggerType === "all_new" && (
                  <label className="flex items-center gap-2">
                    <Field type="checkbox" name="isDefault" className="rounded" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Activar por defecto para nuevos contactos</span>
                  </label>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
                <button type="button" onClick={handleClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                  Cancelar
                </button>
                <button type="submit" disabled={isSubmitting} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {flow ? "Guardar" : "Crear"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default BotFlowModal;
